'use client'
import { CalculatorSection } from 'components/DtiRadialMeter/CalculatorSection'
import Section from 'components/Section'
import styles from './DTIRadialCalculator.module.scss'
import { useRef, useState } from 'react'
import { TrackingWrapper } from 'components/Tracking/TrackingWrapper'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { mainProductConfig } from 'constants/mainProductConfig'
import { GOOD_APP_LINKS, MOLO_APP_LINKS } from 'components/DtiRadialMeter/tools'

const APP_STORE_TRACKING = [
  {
    key: 'goodAppCard',
    app: 'Good App',
    listName: 'DTI V2 GOOD APP CARD',
    linkPrefix: 'good',
    defaultLinks: GOOD_APP_LINKS,
  },
  {
    key: 'moloAppCard',
    app: 'Molo App',
    listName: 'DTI V2 MOLO APP CARD',
    linkPrefix: 'molo',
    defaultLinks: MOLO_APP_LINKS,
  },
].flatMap((app) => [
  { ...app, store: 'appStore', storeName: 'App Store', linkProp: `${app.linkPrefix}AppStoreLink` },
  {
    ...app,
    store: 'googlePlay',
    storeName: 'Google Play',
    linkProp: `${app.linkPrefix}GooglePlayLink`,
  },
])

const DTIRadialCalculator = ({ content }) => {
  const sectionVariationObject = {
    ...content?.fields?.experiment,
    variant: content?.fields?.experimentVariation,
  }
  const [formParameters, setFormParameters] = useState({})
  const { fireAndForget } = useTriggerEvents()
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'
  const trackRefs = useRef({
    submitFormCta: null,
    tableCta: null,
    debtReliefCard: null,
    personalLoansCard: null,
    homeLoansCard: null,
    goodAppCard: {},
    moloAppCard: {},
    lowRatioLinkClick: null,
    mediumRatioLinkClick: null,
    highRatioLinkClick: null,
  })

  const configDti =
    (content?.fields?.additionalConfiguration?.jsonContent ??
      content?.fields?.additionalConfiguration?.fields?.jsonContent) ||
    {}
  let args = {
    ...configDti,
    dtiRecommendationProps: {
      ...configDti.dtiRecommendationProps,
      onDebtReliefClick: () => trackRefs?.current?.debtReliefCard?.click(),
      onPersonalLoansClick: () => trackRefs?.current?.personalLoansCard?.click(),
      onHomeLoansClick: () => trackRefs?.current?.homeLoansCard?.click(),
      onGoodAppClick: (store) => trackRefs?.current?.goodAppCard?.[store]?.click(),
      onMoloAppClick: (store) => trackRefs?.current?.moloAppCard?.[store]?.click(),
      onLowRatioLinkClick: () => trackRefs?.current?.lowRatioLinkClick?.click(),
      onMediumRatioLinkClick: () => trackRefs?.current?.mediumRatioLinkClick?.click(),
      onHighRatioLinkClick: () => trackRefs?.current?.highRatioLinkClick?.click(),
    },
    onCalculate: (response) => onCalculateTrack(response),
    onChangeParameters: (response) => setFormParameters(response),
    onSubmitFormTracking: () => trackRefs?.current?.submitFormCta?.click(),
    onTableCtaTracking: () => trackRefs?.current?.tableCta?.click(),
  }

  // API Track Event
  const onCalculateTrack = (response) => {
    fireAndForget({
      event_action: 'app_event',
      track_event: 'tool_response',
      form_expenses: formParameters.expenses,
      form_income: formParameters.income,
      form_ss_amount: formParameters.credit,
      tool_response: response,
    })
  }

  return (
    <Section className={styles.container} data-testid="dti-radial-calculator-section">
      <CalculatorSection {...args} />
      <TrackingWrapper
        data-testid="dti-v2-submit-form-cta-tracking"
        track={{
          list_name: 'DTI V2 SUBMIT FORM CTA',
          event_action: 'button_click',
          click_text: `${
            mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
          } | Calculate`,
          click_type: 'Button Click',
          click_url: 'DTI Tool Submit',
          form_expenses: formParameters.expenses,
          form_income: formParameters.income,
          form_ss_amount: formParameters.credit,
          nav_link_section: 'Tool Form',
          track_event: 'button_click',
          sectionVariationObject,
        }}
      >
        <div ref={(el) => (trackRefs.current.submitFormCta = el)}></div>
      </TrackingWrapper>
      <TrackingWrapper
        data-testid="dti-v2-table-cta-tracking"
        track={{
          list_name: 'DTI V2 TABLE CTA',
          event_action: 'button_click',
          click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']} | ${
            configDti?.dtiTableCtaText || 'Explore Your Options'
          }`,
          click_type: 'Button Click',
          click_url: configDti?.dtiTableCtaUrl,
          form_expenses: formParameters.expenses,
          form_income: formParameters.income,
          form_ss_amount: formParameters.credit,
          nav_link_section: 'Tool Scenario CTA',
          track_event: 'button_click',
          sectionVariationObject,
        }}
      >
        <div ref={(el) => (trackRefs.current.tableCta = el)}></div>
      </TrackingWrapper>
      <TrackingWrapper
        data-testid="dti-v2-debt-relief-card-tracking"
        track={{
          list_name: 'DTI V2 DEBT RELIEF CARD',
          event_action: 'button_click',
          click_id: 'Debt Relief',
          click_text: `${
            mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
          } | Debt Relief`,
          click_type: 'Button Click',
          click_url: configDti?.dtiRecommendationProps?.debtReliefCardLink,
          form_expenses: formParameters.expenses,
          form_income: formParameters.income,
          form_ss_amount: formParameters.credit,
          nav_link_section: 'Tool Scenario CTA',
          track_event: 'button_click',
          sectionVariationObject,
        }}
      >
        <div ref={(el) => (trackRefs.current.debtReliefCard = el)}></div>
      </TrackingWrapper>
      <TrackingWrapper
        data-testid="dti-v2-personal-loans-card-tracking"
        track={{
          list_name: 'DTI V2 PERSONAL LOANS CARD',
          event_action: 'button_click',
          click_id: 'Personal Loans',
          click_text: `${
            mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
          } | Personal Loans`,
          click_type: 'Button Click',
          click_url: configDti?.dtiRecommendationProps?.personalLoansCardLink,
          form_expenses: formParameters.expenses,
          form_income: formParameters.income,
          form_ss_amount: formParameters.credit,
          nav_link_section: 'Tool Scenario CTA',
          track_event: 'button_click',
          sectionVariationObject,
        }}
      >
        <div ref={(el) => (trackRefs.current.personalLoansCard = el)}></div>
      </TrackingWrapper>
      <TrackingWrapper
        data-testid="dti-v2-home-loans-card-tracking"
        track={{
          list_name: 'DTI V2 HOME LOANS CARD',
          event_action: 'button_click',
          click_id: 'Home Loan',
          click_text: `${
            mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
          } | Home Loan`,
          click_type: 'Button Click',
          click_url: configDti?.dtiRecommendationProps?.homeLoansCardLink,
          form_expenses: formParameters.expenses,
          form_income: formParameters.income,
          form_ss_amount: formParameters.credit,
          nav_link_section: 'Tool Scenario CTA',
          track_event: 'button_click',
          sectionVariationObject,
        }}
      >
        <div ref={(el) => (trackRefs.current.homeLoansCard = el)}></div>
      </TrackingWrapper>
      {APP_STORE_TRACKING.map(
        ({ key, app, listName, store, storeName, linkProp, defaultLinks }) => (
          <TrackingWrapper
            key={`${key}-${store}`}
            data-testid={`dti-v2-${key}-${store}-tracking`}
            track={{
              list_name: listName,
              event_action: 'button_click',
              click_id: `${app} | ${storeName}`,
              click_text: `${
                mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
              } | ${app} | ${storeName}`,
              click_type: 'Button Click',
              click_url: configDti?.dtiRecommendationProps?.[linkProp] || defaultLinks[store],
              form_expenses: formParameters.expenses,
              form_income: formParameters.income,
              form_ss_amount: formParameters.credit,
              nav_link_section: 'Tool Scenario CTA',
              track_event: 'button_click',
              sectionVariationObject,
            }}
            trackAsConversion={true}
          >
            <div ref={(el) => (trackRefs.current[key][store] = el)}></div>
          </TrackingWrapper>
        )
      )}
      <TrackingWrapper
        data-testid="dti-v2-low-ratio-link-click-tracking"
        track={{
          list_name: 'DTI V2 LOW RATIO LINK',
          click_id: 'Explore your options',
          event_action: 'link_click',
          click_text: `${
            mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
          } | Explore your options`,
          click_type: 'Link Click',
          click_url: configDti?.dtiRecommendationProps?.lowRatioLinkHref,
          form_expenses: formParameters.expenses,
          form_income: formParameters.income,
          form_ss_amount: formParameters.credit,
          nav_link_section: 'Tool Scenario CTA',
          track_event: 'link_click',
          sectionVariationObject,
        }}
      >
        <div ref={(el) => (trackRefs.current.lowRatioLinkClick = el)}></div>
      </TrackingWrapper>
      <TrackingWrapper
        data-testid="dti-v2-medium-ratio-link-click-tracking"
        track={{
          list_name: 'DTI V2 MEDIUM RATIO LINK',
          click_id: 'Explore your options',
          event_action: 'link_click',
          click_text: `${
            mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
          } | Explore your options`,
          click_type: 'Link Click',
          click_url: configDti?.dtiRecommendationProps?.mediumRatioLinkHref,
          form_expenses: formParameters.expenses,
          form_income: formParameters.income,
          form_ss_amount: formParameters.credit,
          nav_link_section: 'Tool Scenario CTA',
          track_event: 'link_click',
          sectionVariationObject,
        }}
      >
        <div ref={(el) => (trackRefs.current.mediumRatioLinkClick = el)}></div>
      </TrackingWrapper>
      <TrackingWrapper
        data-testid="dti-v2-high-ratio-link-click-tracking"
        track={{
          list_name: 'DTI V2 HIGH RATIO LINK',
          click_id: 'Get your debt plan',
          event_action: 'link_click',
          click_text: `${
            mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
          } | Get your debt plan`,
          click_type: 'Link Click',
          click_url: configDti?.dtiRecommendationProps?.highRatioLinkHref,
          form_expenses: formParameters.expenses,
          form_income: formParameters.income,
          form_ss_amount: formParameters.credit,
          nav_link_section: 'Tool Scenario CTA',
          track_event: 'link_click',
          sectionVariationObject,
        }}
      >
        <div ref={(el) => (trackRefs.current.highRatioLinkClick = el)}></div>
      </TrackingWrapper>
    </Section>
  )
}
export default DTIRadialCalculator
